import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Check } from "lucide-react";
import { AnimatedWords, Reveal } from "@/components/motion";

const WITHOUT = [
  "\u201cI\u2019ll go tomorrow.\u201d",
  "Guessing your way through every workout",
  "No one pushing you on the days you want to quit",
  "Spending months with little to no progress",
  "Losing motivation before seeing results",
];

const WITH = [
  "A gym partner who keeps you accountable",
  "More consistency. Better workouts. Faster results.",
  "Learn from someone who's already where you want to be",
  "Push each other to show up, lift harder, and stay committed",
  "Turn fitness into a lifestyle instead of a phase",
];

const item = {
  hidden: { opacity: 0, x: -16 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export default function BeforeAfter() {
  return (
    <section id="before-after" data-testid="before-after-section" className="relative bg-[#0A0A0A] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-teal" />
          <span className="font-condensed text-base tracking-[0.35em] text-teal">BEFORE &amp; AFTER</span>
        </div>
        <h2 className="mb-16 font-display text-5xl leading-[0.9] uppercase sm:text-6xl md:text-7xl">
          <AnimatedWords text="Why Trainova" />{" "}
          <span className="accent-teal">
            <AnimatedWords text="works" delay={0.15} />
          </span>
        </h2>

        <div className="grid gap-4 md:grid-cols-2">
          {/* WITHOUT */}
          <Reveal>
            <div
              data-testid="without-trainova"
              className="h-full border border-white/10 bg-[#0d0d0d] p-8 md:p-10"
            >
              <div className="mb-8 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center border border-red-500/40 text-red-500">
                  <X size={20} />
                </span>
                <h3 className="font-condensed text-2xl tracking-widest text-zinc-400 md:text-3xl">
                  WITHOUT TRAINOVA
                </h3>
              </div>
              <ul className="flex flex-col gap-5">
                {WITHOUT.map((t, i) => (
                  <motion.li
                    key={i}
                    custom={i}
                    variants={item}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    className="flex items-start gap-4 border-b border-white/5 pb-5"
                  >
                    <X size={20} className="mt-0.5 flex-shrink-0 text-red-500/70" />
                    <span className="text-base text-zinc-500 line-through decoration-red-500/30 md:text-lg">
                      {t}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* WITH */}
          <Reveal delay={0.1}>
            <div
              data-testid="with-trainova"
              className="relative h-full overflow-hidden border border-teal/40 bg-[#0c1211] p-8 md:p-10"
            >
              <div className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-teal/10 blur-3xl" />
              <div className="mb-8 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center bg-teal text-black">
                  <Check size={20} />
                </span>
                <h3 className="font-condensed text-2xl tracking-widest text-white md:text-3xl">
                  WITH TRAINOVA
                </h3>
              </div>
              <ul className="flex flex-col gap-5">
                {WITH.map((t, i) => (
                  <motion.li
                    key={i}
                    custom={i}
                    variants={item}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    className="flex items-start gap-4 border-b border-white/5 pb-5"
                  >
                    <Check size={20} className="mt-0.5 flex-shrink-0 text-teal" />
                    <span className="text-base text-white md:text-lg">{t}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-10 flex justify-center">
            <Link
              to="/download"
              data-testid="before-after-cta"
              className="group inline-flex items-center gap-3 bg-teal px-8 py-4 font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.03]"
            >
              Make the switch — Download Now
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
